import type {
  AnalysisClassificationRule,
  AnalysisContract,
  AnalysisFieldDefinition,
  AnalysisRequestedDimension,
} from '../../types/workstream';

const SUMMARY_DIMENSION_COUNT = 3;

function Question({ dimension }: { dimension: AnalysisRequestedDimension }) {
  return (
    <li>
      <span className="font-medium text-slate-800">{dimension.label}:</span>{' '}
      {dimension.description}
      {dimension.exclusion_reason && (
        <p className="mt-1 text-xs text-amber-800">Outside this analysis: {dimension.exclusion_reason}</p>
      )}
    </li>
  );
}

function classificationLabel(
  rule: AnalysisClassificationRule,
  fields: Map<string, AnalysisFieldDefinition>,
): string | null {
  const field = fields.get(rule.target_field);
  if (!field) return null;
  // A boolean field's `allowed_values` is empty (it is not an enum), so the
  // enum lookup below always misses and every boolean rule fell back to the
  // bare `field.label` — "X = true" and "X = false" then rendered identically.
  if (field.value_type === 'boolean') return `${field.label} — ${rule.target_value ? 'Yes' : 'No'}`;
  const option = field.allowed_values.find((candidate) => candidate.value === rule.target_value);
  return option ? `${field.label} — ${option.definition}` : field.label;
}

export function AnalysisSetup({ contract }: { contract: AnalysisContract }) {
  const scope = contract.research_scope;
  const fields = new Map(contract.fields.map((field) => [field.name, field]));
  const summaryQuestions = contract.requested_dimensions.slice(0, SUMMARY_DIMENSION_COUNT);
  const detailedQuestions = contract.requested_dimensions.slice(SUMMARY_DIMENSION_COUNT);

  return (
    <section
      className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-4 space-y-4"
      aria-labelledby="analysis-setup-heading"
    >
      <div>
        <h3 id="analysis-setup-heading" className="text-base font-semibold text-slate-900">
          What we'll analyze
        </h3>
        <p className="mt-1 text-sm text-slate-600">
          This research setup is included in this plan approval.
        </p>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-slate-900">Scope</h4>
        <p className="mt-1 text-sm text-slate-700">
          {scope.population} · {scope.topic} · {scope.time_scope}
        </p>
        <p className="mt-1 text-sm text-slate-600">
          {scope.approval_scope} · {scope.version_scope}
        </p>
        <p className="mt-1 text-sm text-slate-600">Compared as: {contract.record_granularity.label}</p>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-slate-900">Questions to answer</h4>
        <ul className="mt-1 space-y-1 text-sm text-slate-700 list-disc pl-5">
          {summaryQuestions.map((dimension) => <Question key={dimension.label} dimension={dimension} />)}
        </ul>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-slate-900">Classification rules</h4>
        {contract.classification_rules.length > 0 ? (
          <ul className="mt-1 space-y-2 text-sm text-slate-700 list-disc pl-5">
            {contract.classification_rules.map((rule, index) => {
              // Shown once per field, under its first rule only — the same
              // sentence repeated under every rule for that field added
              // nothing and read as duplicated.
              const isFirstRuleForField = contract.classification_rules
                .findIndex((other) => other.target_field === rule.target_field) === index;
              return (
                <li key={`${rule.target_field}-${index}`}>
                  {classificationLabel(rule, fields) && (
                    <span className="font-medium text-slate-800">{classificationLabel(rule, fields)}: </span>
                  )}
                  {rule.description}
                  {isFirstRuleForField && fields.get(rule.target_field)?.evidence.description && (
                    <p className="mt-1 text-xs text-slate-600">{fields.get(rule.target_field)?.evidence.description}</p>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="mt-1 text-sm text-slate-700">
            Findings are reported with their evidence state. Inconclusive results stay unknown,
            unsupported, conflicting or not applicable, with the reason explained.
          </p>
        )}
      </div>

      <details className="group rounded-md border border-indigo-100 bg-white/70 p-3">
        <summary className="cursor-pointer text-sm font-medium text-indigo-800">
          View research details
        </summary>
        <div className="mt-3 space-y-4 text-sm text-slate-700">
          {detailedQuestions.length > 0 && (
            <div>
              <h5 className="font-semibold text-slate-800">Additional questions</h5>
              <ul className="mt-1 space-y-1 list-disc pl-5">
                {detailedQuestions.map((dimension) => <Question key={dimension.label} dimension={dimension} />)}
              </ul>
            </div>
          )}

          {(scope.inclusion_criteria.length > 0 || scope.exclusion_criteria.length > 0) && (
            <div>
              <h5 className="font-semibold text-slate-800">What counts</h5>
              <ul className="mt-1 space-y-1 list-disc pl-5">
                {scope.inclusion_criteria.map((criterion, index) => <li key={`include-${index}`}>{criterion}</li>)}
                {scope.exclusion_criteria.map((criterion, index) => (
                  <li key={`exclude-${index}`}>Excluded: {criterion}</li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h5 className="font-semibold text-slate-800">How each answer is assessed</h5>
            <ul className="mt-1 space-y-2">
              {contract.fields.map((field) => (
                <li key={field.name}>
                  <span className="font-medium">{field.label}:</span> {field.definition}
                  <p className="text-xs text-slate-600">{field.evidence.description}</p>
                  <p className="text-xs text-slate-600">Applies when: {field.applicability}</p>
                  {field.classification_criteria && (
                    <p className="text-xs text-slate-600">Assessment: {field.classification_criteria}</p>
                  )}
                  {field.evidence.search_terms && field.evidence.search_terms.length > 0 && (
                    <p className="text-xs text-slate-600">
                      Search terms: {field.evidence.search_terms.map((term) => `"${term}"`).join(', ')}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h5 className="font-semibold text-slate-800">When evidence is inconclusive</h5>
            <ul className="mt-1 space-y-1 list-disc pl-5">
              <li>{contract.answer_rules.unknown}</li>
              <li>{contract.answer_rules.unsupported}</li>
              <li>{contract.answer_rules.conflicting}</li>
              <li>{contract.answer_rules.inapplicable}</li>
              {contract.fields.some((field) => field.evidence.search_terms && field.evidence.search_terms.length > 0) && (
                <li>{'Not found after search: none of the listed terms appears in the searched documents; this is not proof of absence.'}</li>
              )}
            </ul>
          </div>
        </div>
      </details>
    </section>
  );
}
